import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { Heart, Clock, ShieldCheck, MessageCircle } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwZWFjZWZ1bCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODc4MTk3OTB8MA&ixlib=rb-4.1.0&q=85&w=1400";

const reassurances = [
  { icon: Heart, title: "Nessuna pressione", desc: "Non ti chiederemo mai di decidere in fretta. I tuoi tempi sono i nostri tempi." },
  { icon: ShieldCheck, title: "Tutela integrale", desc: "Un protocollo pensato per proteggere te e la tua famiglia, non solo l'operazione." },
  { icon: Clock, title: "Chiarezza sui tempi", desc: "Ti spieghiamo con parole semplici cosa succede, passo dopo passo." },
  { icon: MessageCircle, title: "Riservatezza", desc: "Un contatto discreto e riservato. La tua situazione resta tra noi." },
];

export default function Proprietari() {
  useSeo({
    title: "Proprietari in difficoltà — saldo e stralcio con tutela",
    description:
      "Hai un immobile in difficoltà, un debito o un'esecuzione in corso? Ti aiutiamo con dignità e senza pressione: saldo e stralcio, tutela integrale, contatto riservato.",
  });

  return (
    <div>
      {/* SOFTER HERO */}
      <section className="relative pt-20 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D1B2A]/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A] to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <span className="label-eyebrow">Sei un proprietario in difficoltà?</span>
          <Reveal>
            <h1 className="font-display font-light text-4xl lg:text-5xl text-white mt-5 leading-tight">
              Respira. C'è una via d'uscita, con dignità.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-gray-200 text-lg mt-8 leading-relaxed max-w-2xl mx-auto">
              Un debito, un'ipoteca, un'asta in arrivo. Sappiamo quanto pesa. Non siamo qui per
              approfittarne: siamo qui per aiutarti a trovare la soluzione migliore possibile, con
              rispetto e senza fretta.
            </p>
          </Reveal>
        </div>
      </section>

      {/* REASSURANCES */}
      <section className="bg-[#1E2229] py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reassurances.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-[#0D1B2A] rounded-lg p-7 h-full border border-white/5">
                  <r.icon className="text-[#C5A059]" size={28} strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-white mt-4">{r.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS */}
      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-light text-3xl text-white">Cosa succede, in parole semplici</h2>
            <div className="space-y-6 mt-8">
              {[
                "Ci racconti la tua situazione. Ascoltiamo, senza giudicare e senza impegno.",
                "Analizziamo il debito e le tempistiche, e ti spieghiamo le opzioni reali.",
                "Se c'è margine, trattiamo con la banca un saldo e stralcio sostenibile.",
                "Chiudiamo la posizione: tu esci dal debito, con più serenità.",
              ].map((t, i) => (
                <div key={i} className="flex gap-5 items-start">
                  <span className="font-serif-claim text-3xl text-[#C5A059] leading-none">0{i + 1}</span>
                  <p className="text-gray-300 leading-relaxed pt-1">{t}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM — empathetic */}
      <section className="bg-[#1E2229] py-24">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="bg-[#0D1B2A] rounded-lg p-8 lg:p-12 border border-white/5">
              <h2 className="font-display font-light text-3xl text-white">Scrivici in modo riservato</h2>
              <p className="text-gray-400 mt-3 leading-relaxed">
                Ti ricontatteremo con discrezione. Nessuna pressione, nessun impegno.
              </p>
              <div className="mt-8">
                <LeadForm
                  fixedProfile="Proprietario"
                  businessLine="Saldo e Stralcio"
                  sourcePage="Proprietari in difficoltà"
                  variant="empathetic"
                  submitLabel="Chiedi aiuto in modo riservato"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
